document.addEventListener('alpine:init', () => {
  Alpine.data('HTMLVideo', () => ({
    init() {
      const video = this.$refs.video;

      // Immediately hide native controls and prepare video for autoplay
      this.prepareVideoForAutoplay(video);

      video.addEventListener('play', () => {
        this.$root.dispatchEvent(
          new CustomEvent('playing', {
            bubbles: true,
          })
        );
        // Mark video as playing to help with CSS
        video.closest('.background-video')?.classList.add('video-playing');
      });

      video.addEventListener('pause', () => {
        this.$root.dispatchEvent(
          new CustomEvent('paused', {
            bubbles: true,
          })
        );
      });

      // Safari (especially mobile) won't autoplay inserted videos
      // Need to handle promise rejection and retry
      if (video.autoplay) {
        // Try immediately
        this.attemptPlay();

        // Also try after next tick and after a short delay for Safari
        this.$nextTick(() => {
          this.attemptPlay();
        });

        // Safari sometimes needs a longer delay after page load
        setTimeout(() => {
          this.attemptPlay();
        }, 100);

        // Additional retry at 300ms for slower Safari loads
        setTimeout(() => {
          this.attemptPlay();
        }, 300);

        // Also try when the video is ready to play
        video.addEventListener('canplay', () => {
          this.attemptPlay();
        }, { once: true });

        // And when the video has loaded enough data
        video.addEventListener('loadeddata', () => {
          this.attemptPlay();
        }, { once: true });

        // Try on loadedmetadata as well (earliest Safari can play)
        video.addEventListener('loadedmetadata', () => {
          this.attemptPlay();
        }, { once: true });
      }
    },
    prepareVideoForAutoplay(video) {
      // Forcefully remove controls attribute
      video.removeAttribute('controls');
      video.controls = false;

      // Set all required attributes for Safari autoplay
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x-webkit-airplay', 'deny');

      // Disable picture-in-picture which can show controls
      video.disablePictureInPicture = true;

      // Set preload to auto to start loading immediately
      video.preload = 'auto';

      // Force Safari to load the video
      video.load();

      // Add class to parent for CSS targeting
      video.closest('.background-video')?.classList.add('video-prepared');
    },
    attemptPlay() {
      const video = this.$refs.video;
      if (!video) return;

      // Ensure video is muted for autoplay (Safari requirement)
      video.muted = true;

      // Ensure playsinline is set (Safari iOS requirement)
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Safari may reject play() if video isn't ready or visible
          // Retry after a short delay
          if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
            setTimeout(() => {
              video.play().catch(() => {
                // If still failing, try on next user interaction
                const playOnInteraction = () => {
                  video.play();
                  document.removeEventListener('touchstart', playOnInteraction);
                  document.removeEventListener('click', playOnInteraction);
                };
                document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true });
                document.addEventListener('click', playOnInteraction, { once: true });
              });
            }, 500);
          }
        });
      }
    },
    play() {
      const video = this.$refs.video;
      if (!video) return;

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Silently handle rejection
        });
      }
    },
    pause() {
      if (this.$refs.video) {
        this.$refs.video.pause();
      }
    },
  }));
});
